import { projectCallableLetterTemplateTranslationMetadata } from './letterTemplateTranslationMetadata.js';

export const ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE = 25;
export const ADMIN_PROSPECT_LETTER_TEMPLATE_NAME_MAX = 120;
export const ADMIN_PROSPECT_LETTER_TEMPLATE_LANGUAGE_V1 = 'fr';

function attachCallableTranslation(request, translation) {
  const projected = projectCallableLetterTemplateTranslationMetadata(translation);
  if (projected) {
    request.translation = projected;
  }
  return request;
}

function clampPageSize(pageSize, fallback) {
  const size = Number(pageSize);
  return Number.isFinite(size) && size > 0 ? Math.floor(size) : fallback;
}

export function generateAdminProspectLetterTemplateOperationId() {
  try {
    const webCrypto = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
    if (webCrypto && typeof webCrypto.randomUUID === 'function') {
      return webCrypto.randomUUID.call(webCrypto);
    }
  } catch {
    // Some browsers expose randomUUID but throw when invoked (secure-context / binding issues).
  }
  return `letter-template-op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {{
 *   operationId: string,
 *   name: string,
 *   bilingual: boolean,
 *   documents: { fr: object, en?: object },
 *   translation?: object,
 * }} params
 */
export function buildCreateAdminProspectLetterTemplateRichRequest({
  operationId,
  name,
  bilingual,
  documents,
  translation,
}) {
  const request = {
    operationId: String(operationId ?? '').trim(),
    name: String(name ?? '').trim(),
    bilingual: Boolean(bilingual),
    documents,
  };
  return attachCallableTranslation(request, translation);
}

/**
 * @param {{
 *   operationId: string,
 *   templateId: string,
 *   baseVersion: number,
 *   bilingual: boolean,
 *   documents: { fr: object, en?: object },
 *   translation?: object,
 * }} params
 */
export function buildCreateAdminProspectLetterTemplateRichVersionRequest({
  operationId,
  templateId,
  baseVersion,
  bilingual,
  documents,
  translation,
}) {
  const request = {
    operationId: String(operationId ?? '').trim(),
    templateId: String(templateId ?? '').trim(),
    baseVersion: Number(baseVersion),
    bilingual: Boolean(bilingual),
    documents,
  };
  return attachCallableTranslation(request, translation);
}

/**
 * @param {{ operationId: string, contentType: string, base64: string }} params
 */
export function buildUploadAdminProspectLetterTemplateAssetRequest({
  operationId,
  contentType,
  base64,
}) {
  return {
    operationId: String(operationId ?? '').trim(),
    contentType: String(contentType ?? '').trim(),
    base64: String(base64 ?? ''),
  };
}

/**
 * @param {{ assetId: string }} params
 */
export function buildGetAdminProspectLetterTemplateAssetPreviewUrlRequest({ assetId }) {
  return { assetId: String(assetId ?? '').trim() };
}

/**
 * @param {{ document: object }} params
 */
export function buildTranslateAdminProspectLetterTemplateToEnglishRequest({ document }) {
  return { document };
}

/**
 * Synthetic-only PDF preview — unsaved rich draft only.
 * Never includes prospect, owner, campaign, token, URL, or publicAccess fields.
 *
 * @param {{
 *   bilingual: boolean,
 *   documents: { fr: object, en?: object },
 *   translation?: object,
 * }} params
 */
export function buildPreviewAdminProspectLetterTemplatePdfRequest({
  bilingual,
  documents,
  translation,
}) {
  const request = {
    bilingual: Boolean(bilingual),
    documents,
  };
  return attachCallableTranslation(request, translation);
}

/**
 * @param {{ operationId: string, name: string, language?: string, document: object }} params
 */
export function buildCreateAdminProspectLetterTemplateRequest({
  operationId,
  name,
  language = ADMIN_PROSPECT_LETTER_TEMPLATE_LANGUAGE_V1,
  document,
}) {
  return {
    operationId: String(operationId ?? '').trim(),
    name: String(name ?? '').trim(),
    language: String(language ?? ADMIN_PROSPECT_LETTER_TEMPLATE_LANGUAGE_V1).trim(),
    document,
  };
}

/**
 * @param {{
 *   operationId: string,
 *   templateId: string,
 *   baseVersion: number,
 *   document: object,
 * }} params
 */
export function buildCreateAdminProspectLetterTemplateVersionRequest({
  operationId,
  templateId,
  baseVersion,
  document,
}) {
  return {
    operationId: String(operationId ?? '').trim(),
    templateId: String(templateId ?? '').trim(),
    baseVersion: Number(baseVersion),
    document,
  };
}

/**
 * @param {{
 *   operationId: string,
 *   templateId: string,
 *   name: string,
 *   version?: number | null,
 * }} params
 */
export function buildDuplicateAdminProspectLetterTemplateRequest({
  operationId,
  templateId,
  name,
  version,
}) {
  const request = {
    operationId: String(operationId ?? '').trim(),
    templateId: String(templateId ?? '').trim(),
    name: String(name ?? '').trim(),
  };
  if (version != null && Number.isFinite(Number(version))) {
    request.version = Number(version);
  }
  return request;
}

/**
 * @param {{ operationId: string, templateId: string }} params
 */
export function buildArchiveAdminProspectLetterTemplateRequest({ operationId, templateId }) {
  return {
    operationId: String(operationId ?? '').trim(),
    templateId: String(templateId ?? '').trim(),
  };
}

/**
 * @param {{ pageSize?: number, pageToken?: string | null }} [params]
 */
export function buildListAdminProspectLetterTemplatesRequest({
  pageSize = ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    pageSize: clampPageSize(pageSize, ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * @param {{ templateId: string, version?: number | null }} params
 */
export function buildGetAdminProspectLetterTemplateRequest({ templateId, version }) {
  const request = { templateId: String(templateId ?? '').trim() };
  if (version != null && Number.isFinite(Number(version))) {
    request.version = Number(version);
  }
  return request;
}

/**
 * @param {{ templateId: string, pageSize?: number, pageToken?: string | null }} params
 */
export function buildListAdminProspectLetterTemplateVersionsRequest({
  templateId,
  pageSize = ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE,
  pageToken,
} = {}) {
  const request = {
    templateId: String(templateId ?? '').trim(),
    pageSize: clampPageSize(pageSize, ADMIN_PROSPECT_LETTER_TEMPLATES_PAGE_SIZE),
  };
  if (pageToken) request.pageToken = String(pageToken);
  return request;
}

/**
 * Keeps one operationId + exact payload across transient retries for the same logical write.
 *
 * @returns {{
 *   reset: () => void,
 *   discard: () => void,
 *   hasAttempt: () => boolean,
 *   getAttemptPayload: () => object | null,
 *   resolveAttemptPayload: (params: {
 *     fingerprint: string,
 *     build: (operationId: string) => object,
 *     generateOperationId?: () => string,
 *   }) => object,
 * }}
 */
export function createAdminProspectLetterTemplateWriteAttempt() {
  let operationId = null;
  let attemptPayload = null;
  let frozenFingerprint = null;

  return {
    reset() {
      operationId = null;
      attemptPayload = null;
      frozenFingerprint = null;
    },
    discard() {
      operationId = null;
      attemptPayload = null;
      frozenFingerprint = null;
    },
    hasAttempt() {
      return attemptPayload != null;
    },
    getAttemptPayload() {
      return attemptPayload;
    },
    resolveAttemptPayload({
      fingerprint,
      build,
      generateOperationId = generateAdminProspectLetterTemplateOperationId,
    }) {
      if (attemptPayload && frozenFingerprint === fingerprint) {
        return attemptPayload;
      }
      operationId = generateOperationId();
      attemptPayload = build(operationId);
      frozenFingerprint = fingerprint;
      return attemptPayload;
    },
  };
}
